import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Loader2, Sparkles, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'paisley_ai_session_id';

const SUGGESTIONS = [
  'What can I do in Paisley this weekend?',
  'Tell me about Paisley Abbey',
  "What's the Paisley Pattern?",
  'Where can I eat in town?',
];

const Bubble = ({ role, content }) => (
  <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div
      className={
        role === 'user'
          ? 'max-w-[85%] bg-slate-900 text-white rounded-2xl rounded-tr-md px-4 py-2.5 text-sm leading-relaxed'
          : 'max-w-[85%] glass-strong text-slate-800 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap'
      }
      data-testid={`chat-bubble-${role}`}
    >
      {content}
    </div>
  </div>
);

const AIChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || '');
  const scrollRef = useRef(null);

  // Rehydrate prior conversation if we have a session id
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/history/${sessionId}`);
        if (data?.messages?.length) setMessages(data.messages);
      } catch (e) {
        // ignore — fresh start
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? draft).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: 'user', content: message }]);
    setDraft('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message,
        session_id: sessionId || undefined,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', content: data.reply || '...' }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sorry, I'm having trouble right now. Please try again.";
      setMessages((m) => [...m, { role: 'assistant', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const resetChat = () => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId('');
    setMessages([]);
  };

  return (
    <section className="relative py-20" data-testid="ai-chat-section">
      <div className="aurora-orb w-[480px] h-[480px] -top-20 left-10 bg-sky-300/40" />
      <div className="aurora-orb w-[420px] h-[420px] bottom-0 right-10 bg-fuchsia-200/40" />

      <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
        {/* Left intro */}
        <div>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-5">
            <Sparkles size={13} className="text-sky-500" />
            <span className="text-[11px] uppercase tracking-widest font-bold text-sky-600">Paisley AI · Live</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-[-0.04em] text-slate-900 leading-[1.05]">
            Ask me <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-500 via-indigo-500 to-fuchsia-500 bg-clip-text text-transparent">anything</span> about Paisley.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed text-base md:text-lg max-w-md">
            Powered by Claude — the assistant knows Paisley's history, attractions, the Paisley Pattern, local culture and more. Get a friendly answer in seconds.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={loading}
                className="glass rounded-full px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:text-sky-700 glass-hover disabled:opacity-50"
                data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\W+/g, '-').toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Chat panel */}
        <div className="glass-strong rounded-[2rem] overflow-hidden flex flex-col h-[520px]" data-testid="chat-panel">
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between border-b border-white/30">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-sky-500/30 ring-1 ring-white/40">
                <MessageCircle size={16} className="text-white" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-bold text-slate-900">Paisley AI</p>
                <p className="text-[11px] text-slate-500">Your local Paisley guide</p>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={resetChat}
                className="text-slate-400 hover:text-slate-700 transition-colors"
                title="Start over"
                data-testid="chat-reset-btn"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && !loading && (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-3">
                <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-sky-500/30 ring-1 ring-white/40">
                  <Sparkles size={20} className="text-white" />
                </span>
                <p className="max-w-xs leading-relaxed">Hi! I'm Paisley AI. Ask me about events, history, places to visit, or anything Paisley.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="glass-strong rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-slate-500 inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-4 py-4 border-t border-white/30">
            <div className="flex gap-2 items-end">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask about Paisley…"
                rows={1}
                disabled={loading}
                className="flex-1 glass rounded-2xl px-4 py-3 text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/60 transition resize-none max-h-32"
                data-testid="chat-input"
              />
              <button
                onClick={() => send()}
                disabled={loading || !draft.trim()}
                className="shrink-0 w-11 h-11 rounded-2xl bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white flex items-center justify-center transition-all hover:scale-[1.03] disabled:hover:scale-100"
                data-testid="chat-send-btn"
                title="Send"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Press Enter to send · Shift+Enter for a new line</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIChatBot;
